import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";

const EventDetails = () => {
  // Mock data for demonstration
  const { state } = useLocation();
  const event = state

  const [selectedImage, setSelectedImage] = useState(null);
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);

  // Mock navigate function
  const navigate = (path) => {
    console.log(`Navigating to: ${path}`);
    alert(`Would navigate to: ${path}`);
  };

  // Helper function to format date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, '0');
    const month = date.toLocaleDateString('en-US', { month: 'short' });
    const year = date.getFullYear();
    const dayName = date.toLocaleDateString('en-US', { weekday: 'long' });
    const fullDate = date.toLocaleDateString('en-US', { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
    
    return { day, month, year, dayName, fullDate };
  };

  // Helper function to get a usable image URL (thumbnail for Drive links)
  const getImageUrl = (url) => {
    if (!url) return "";

    // Google Drive link pattern
    const driveMatch = url.match(
      /https:\/\/drive\.google\.com\/file\/d\/([a-zA-Z0-9_-]+)\/?.*/
    );
    if (driveMatch && driveMatch[1]) {
      // Use Google Drive thumbnail
      return `https://drive.google.com/thumbnail?id=${driveMatch[1]}`;
    }

    return url;
  };

  // Handle image modal
  const openImageModal = (imageSrc) => {
    setSelectedImage(imageSrc);
    setIsImageModalOpen(true);
  };

  const closeImageModal = () => {
    setIsImageModalOpen(false);
    setSelectedImage(null);
  };

  // Close modal on escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape") {
        closeImageModal();
      }
    };

    if (isImageModalOpen) {
      document.addEventListener("keydown", handleEscape);
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = "unset";
    };
  }, [isImageModalOpen]);

  // Handle otherImages
  const otherImages =
    event.otherImages && typeof event.otherImages === "string"
      ? event.otherImages
          .split(",")
          .map((link) => link.trim())
          .filter(Boolean)
      : [];

  const dateFormatted = formatDate(event.dateObj);

  return (
    <div className="min-h-screen relative overflow-hidden bg-gradient-to-br from-blue-50 via-white to-purple-50">
      {/* Enhanced Animated Background */}
      <div className="fixed inset-0 -z-10">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-blue-100 via-transparent to-purple-100 opacity-60"></div>
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-blue-200/30 rounded-full mix-blend-multiply filter blur-3xl animate-pulse"></div>
        <div className="absolute top-3/4 right-1/4 w-96 h-96 bg-purple-200/30 rounded-full mix-blend-multiply filter blur-3xl animate-pulse"></div>
        <div className="absolute top-1/2 left-1/2 w-96 h-96 bg-pink-200/20 rounded-full mix-blend-multiply filter blur-3xl animate-pulse"></div>
      </div>

      {/* Title Section */}
      <section className="relative pt-28 pb-14 text-center overflow-hidden">
        <div className="max-w-7xl mx-auto px-6">
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-extrabold tracking-tight mb-6 text-black animate-fade-in-up">
            {event.title}
          </h1>
        </div>
      </section>

      {/* Main Content - New Layout */}
      <section className="relative z-10 max-w-7xl mx-auto px-6 pb-12">
        <div className="bg-white/70 backdrop-blur-xl rounded-3xl shadow-2xl border border-white/20 overflow-hidden">
          
          {/* Image and Side Content Grid */}
          <div className="grid lg:grid-cols-3 gap-0">
            {/* Main Image - Left Side */}
            <div className="lg:col-span-2 relative group overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-black/5 to-black/20 z-10"></div>
              <img
                src={getImageUrl(event.imageUrl)}
                alt={event.title}
                className="w-full h-80 md:h-[500px] lg:h-[600px] object-cover cursor-zoom-in transition-transform duration-700 group-hover:scale-105"
                onClick={() => openImageModal(getImageUrl(event.imageUrl))}
              />
              
              {/* Floating expand button */}
              <div className="absolute top-6 right-6 z-20 opacity-0 group-hover:opacity-100 transition-all duration-300 transform translate-y-2 group-hover:translate-y-0">
                <div className="bg-white/90 backdrop-blur-md rounded-full p-3 shadow-lg border border-white/50">
                  <svg className="w-5 h-5 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
                  </svg>
                </div>
              </div>

              {/* Image overlay gradient */}
              <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-black/30 to-transparent z-10"></div>
            </div>

            {/* Right Side Content */}
            <div className="lg:col-span-1 p-8 lg:p-10 flex flex-col">
              
              {/* Location Card */}
              {event.location && (
                <div className="mb-8">
                  <div className="bg-gradient-to-r from-blue-50/50 to-purple-50/50 backdrop-blur-sm rounded-2xl p-6 border border-white/30">
                    <div className="flex items-start gap-4">
                      <div className="flex-shrink-0">
                        <div className="w-12 h-12 bg-gray-900 rounded-2xl flex items-center justify-center shadow-lg">
                          <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                          </svg>
                        </div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-bold text-gray-500 uppercase tracking-widest mb-1">
                          Event Location
                        </p>
                        <p className="text-lg font-semibold text-gray-900 leading-snug">
                          {event.location}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Date Card - Positioned after location */}
              {event.dateObj && (
                <div className="mb-8">
                  <div className="bg-gradient-to-br from-blue-50/80 to-purple-50/80 backdrop-blur-sm rounded-2xl p-6 shadow-xl border border-white/50">
                    <div className="flex items-center gap-4">
                      <div className="flex-shrink-0">
                        <div className="w-16 h-16 bg-gray-900 rounded-2xl flex items-center justify-center shadow-lg">
                          <div className="text-center">
                            <div className="text-xl font-bold text-white">
                              {dateFormatted.day}
                            </div>
                            <div className="text-xs text-gray-300 uppercase">
                              {dateFormatted.month}
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-bold text-gray-500 uppercase tracking-widest mb-1">
                          Event Date
                        </p>
                        
                        <p className="text-lg font-semibold text-gray-900">
                          {dateFormatted.fullDate}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* About This Event - Header only on right side */}
              <div className="mb-6">
                <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-4">
                  <div className="w-1.5 h-8 bg-gradient-to-b from-gray-900 to-gray-600 rounded-full"></div>
                  About This Event
                </h3>
              </div>
            </div>
          </div>

          {/* Description Section - Full Width Below Image */}
          <div className="p-8 lg:p-12 border-t border-gray-200/50">
            <div className="prose prose-lg max-w-none">
              <div className="text-gray-700 leading-relaxed whitespace-pre-line text-lg space-y-4">
                {event.description}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Enhanced Gallery Section */}
      {otherImages.length > 0 && (
        <section className="relative z-10 py-16">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-black bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent mb-4">
                Event Gallery
              </h2>
              <div className="w-24 h-1 bg-gradient-to-r from-gray-900 to-gray-600 rounded-full mx-auto"></div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 md:gap-8 auto-rows-fr">
              {otherImages.map((img, idx) => (
                <div
                  key={idx}
                  className="group relative bg-white/60 backdrop-blur-sm rounded-2xl overflow-hidden shadow-xl border border-white/30 hover:shadow-2xl transform hover:scale-[1.02] transition-all duration-500 cursor-zoom-in"
                  onClick={() => openImageModal(img)}
                >
                  <div className="aspect-square relative overflow-hidden rounded-2xl">
                    <img
                      src={img}
                      alt={`Gallery ${idx + 1}`}
                      className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-700"
                      onError={(e) => {
                        e.currentTarget.onerror = null;
                      }}
                    />
                    
                    {/* Overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                    
                    {/* Zoom icon */}
                    <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300 transform scale-75 group-hover:scale-100">
                      <div className="bg-white/90 backdrop-blur-sm rounded-full p-3 shadow-lg">
                        <svg className="w-6 h-6 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
                        </svg>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Enhanced Back Button */}
      <div className="text-center pt-16 pb-20 relative z-10">
        <button
          onClick={() => navigate("/events")}
          className="group relative inline-flex items-center gap-4 px-12 py-5 bg-gray-900 text-white font-bold rounded-2xl shadow-2xl hover:shadow-3xl transform hover:scale-105 transition-all duration-300 overflow-hidden"
        >
          <div className="absolute inset-0 bg-gradient-to-r from-gray-800 to-gray-900 translate-x-[-100%] group-hover:translate-x-0 transition-transform duration-500"></div>
          <svg
            className="w-6 h-6 relative z-10 transform group-hover:-translate-x-2 transition-transform duration-300"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          <span className="relative z-10 text-lg">Back to Events</span>
        </button>
      </div>

      {/* Enhanced Image Modal */}
      {isImageModalOpen && selectedImage && (
        <div className="fixed inset-0 bg-black/90 backdrop-blur-md z-50 flex items-center justify-center p-6">
          <div className="relative max-w-7xl max-h-[90vh] w-full">
            {/* Close button */}
            <button
              onClick={closeImageModal}
              className="absolute top-4 right-4 sm:-top-4 sm:right-0 text-white/80 hover:text-white transition-colors duration-300 z-10 bg-black/20 backdrop-blur-sm rounded-full p-3 hover:bg-black/40"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            
            <div className="bg-white/10 backdrop-blur-sm rounded-3xl border border-white/20">
              <img
                src={selectedImage}
                alt="Enlarged view"
                className="max-w-full max-h-[80vh] object-contain rounded-2xl shadow-2xl mx-auto"
                onClick={closeImageModal}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EventDetails;